import { AddOnRateType, AddOnType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import * as yup from 'yup';

import { nameIdSchemaRequired } from '~constants/regexConst';
import { AddOn } from '~hooks/useAddOns/models';
import { FuelSurchargeScheduleTypeahead } from '~hooks/useFuelSurchargeSchedule/model';

export const addOnRateTypeOptions = [
  {
    id: AddOnRateType.RATE_FOR_EACH,
    name: t('administration.rates.add_ons.rate_for_each'),
  },
  {
    id: AddOnRateType.RATE_PERCENT_OF_TOTAL,
    name: t('administration.rates.add_ons.rate_percent_of_total'),
  },
  {
    id: AddOnRateType.PER_LOAD,
    name: t('administration.rates.add_ons.per_load'),
  },
  {
    id: AddOnRateType.FUEL_SURCHARGE,
    name: t('administration.rates.add_ons.fuel_surcharge'),
  },
];

export const addOnTypeOptions = [
  {
    id: AddOnType.DRIVER_PAY,
    name: t('administration.rates.add_ons.add_on_type_driver_pay'),
  },
  {
    id: AddOnType.INVOICING,
    name: t('administration.rates.add_ons.add_on_type_invoicing'),
  },
  {
    id: AddOnType.JOB,
    name: t('administration.rates.add_ons.add_on_type_job'),
  },
  {
    id: AddOnType.SETTLEMENT,
    name: t('administration.rates.add_ons.add_on_type_settlement'),
  },
  {
    id: AddOnType.DRIVER_DAY,
    name: t('administration.rates.add_ons.add_on_type_driver_day'),
  },
];

export const addOnSchema = yup.object().shape({
  id: yup.string().uuid().notRequired(),
  name: yup
    .string()
    .trim()
    .min(1, `${t('form_validation_errors.required', { field: t('common.description') })}`)
    .required(),
  revenueCostRateType: nameIdSchemaRequired.required(
    `${t('form_validation_errors.required', { field: t('form_fields.rate_type') })}`,
  ),
  rate: yup
    .number()
    .transform((val) => (Number.isNaN(val) ? null : Number(val)))
    .when('revenueCostRateType', ([addOnRateType], schema) => {
      if (!addOnRateType) {
        return schema.notRequired();
      }

      const type = addOnRateType as (typeof addOnRateTypeOptions)[number];

      if (type.id === AddOnRateType.FUEL_SURCHARGE) {
        return schema.notRequired();
      }

      if (type.id === AddOnRateType.RATE_PERCENT_OF_TOTAL) {
        const message = t('form_validation_errors.must_be_between_min_and_max', {
          field: t('administration.rates.default_rate'),
          min: -100,
          max: 100,
        });

        return schema.min(-100, message).max(100, message);
      }

      return schema;
    }),
  quantity: yup
    .number()
    .transform((val) => (Number.isNaN(val) ? null : Number(val)))
    .when('revenueCostRateType', ([addOnRateType], schema) => {
      if (!addOnRateType) {
        return schema.notRequired();
      }

      const type = addOnRateType as (typeof addOnRateTypeOptions)[number];

      if (type.id === AddOnRateType.FUEL_SURCHARGE) {
        return schema.notRequired();
      }

      return schema.notRequired();
    }),
  addOnTypes: yup
    .array()
    .of(nameIdSchemaRequired)
    .min(
      1,
      `${t('form_validation_errors.min_count_one', { field: t('administration.rates.add_ons.add_on_types'), count: 1 })}`,
    )
    .required(),
  externalId: yup.string().trim(),
  accountingId: yup.string().trim(),
  costRateValue: yup
    .number()
    .transform((val) => (Number.isNaN(val) ? null : Number(val)))
    .when('revenueCostRateType', ([addOnRateType], schema) => {
      if (!addOnRateType) {
        return schema.notRequired();
      }

      const type = addOnRateType as (typeof addOnRateTypeOptions)[number];

      if (type.id === AddOnRateType.FUEL_SURCHARGE) {
        return schema.notRequired();
      }

      return schema.notRequired();
    }),
  taxable: yup.boolean().notRequired(),
  fuelSurchargeSchedule: yup
    .object()
    .shape({
      id: yup.string(),
      name: yup.string(),
    })
    .when('revenueCostRateType', ([addOnRateType], schema) => {
      if (!addOnRateType) {
        return schema.notRequired();
      }

      const type = addOnRateType as (typeof addOnRateTypeOptions)[number];

      if (type.id === AddOnRateType.FUEL_SURCHARGE) {
        const message = `${t('form_validation_errors.required', { field: t('administration.rates.add_ons.fuel_surcharge_schedule') })}`;
        return schema
          .required(message)
          .test('has-id', message, (value) => Boolean(value?.id));
      }

      return schema.notRequired();
    }),
});

export const getAddOnFormDefaultValues = (addOn?: AddOn, defaultAddOnName?: string) => {
  const id = addOn?.id;
  const name = addOn?.name ?? defaultAddOnName ?? '';
  const revenueCostRateType = addOn?.revenueCostRateType
    ? addOnRateTypeOptions.find(({ id }) => id === addOn.revenueCostRateType)
    : null;
  const rate =
    addOn?.revenueCostRateType === AddOnRateType.RATE_FOR_EACH ||
    addOn?.revenueCostRateType === AddOnRateType.PER_LOAD
      ? (addOn?.rate ?? '')
      : (addOn?.percentage ?? '');
  const quantity = addOn?.quantity ?? '';
  const externalId = addOn?.externalId ?? '';
  const accountingId = addOn?.accountingId ?? '';
  const costRateValue = addOn?.costRateValue ?? '';
  const taxable = addOn?.taxable ?? false;
  const fuelSurchargeSchedule = addOn?.fuelSurchargeSchedule;

  return {
    id,
    name,
    revenueCostRateType,
    rate,
    quantity,
    addOnTypes: addOnTypeOptions,
    externalId,
    accountingId,
    costRateValue,
    taxable,
    fuelSurchargeSchedule,
  } as unknown as AddOnDTO;
};

export type AddOnDTO = yup.InferType<typeof addOnSchema> & {
  fuelSurchargeSchedule?: FuelSurchargeScheduleTypeahead;
};
